import { BusinessBranding } from '../types';

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  text: string;
  background: string;
  border: string;
  cardBg: string;
  buttonPrimary: string;
  buttonPrimaryHover: string;
  buttonSecondary: string;
  buttonSecondaryHover: string;
  success: string;
  successBg: string;
  error: string;
  errorBg: string;
}

export const getThemeColors = (branding: BusinessBranding | null): ThemeColors => {
  if (!branding) {
    // Default modern theme when no branding data
    return {
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#F59E0B',
      text: '#1F2937',
      background: '#F9FAFB',
      border: '#E5E7EB',
      cardBg: '#FFFFFF',
      buttonPrimary: '#3B82F6',
      buttonPrimaryHover: '#2563EB',
      buttonSecondary: '#6B7280',
      buttonSecondaryHover: '#4B5563',
      success: '#059669',
      successBg: '#D1FAE5',
      error: '#DC2626',
      errorBg: '#FEE2E2'
    };
  }

  // Use colors from branding (these are already processed based on useCustomColors flag)
  return {
    primary: branding.primaryColor,
    secondary: branding.secondaryColor,
    accent: branding.accentColor,
    text: branding.textColor,
    background: branding.backgroundColor,
    border: `${branding.textColor}20`, // 20% opacity
    cardBg: branding.backgroundColor,
    buttonPrimary: branding.primaryColor,
    buttonPrimaryHover: branding.secondaryColor,
    buttonSecondary: `${branding.textColor}40`, // 40% opacity
    buttonSecondaryHover: `${branding.textColor}60`, // 60% opacity
    success: '#059669',
    successBg: '#D1FAE5',
    error: '#DC2626',
    errorBg: '#FEE2E2'
  };
};

export const getThemeStyles = (branding: BusinessBranding | null) => {
  const colors = getThemeColors(branding);
  
  return {
    // Background styles
    pageBg: `bg-gray-50`,
    cardBg: `bg-white`,
    
    // Text styles
    textPrimary: `text-gray-900`,
    textSecondary: `text-gray-600`,
    
    // Border styles
    border: `border-gray-200`,
    
    // Button styles
    buttonPrimary: `bg-blue-600 hover:bg-blue-700 text-white`,
    buttonSecondary: `bg-gray-600 hover:bg-gray-700 text-white`,
    buttonSuccess: `bg-green-600 hover:bg-green-700 text-white`,
    
    // Progress indicator styles
    progressActive: `bg-blue-600 text-white`,
    progressInactive: `bg-gray-200 text-gray-600`,
    
    // Selection styles
    selected: `border-blue-500 bg-blue-50`,
    unselected: `border-gray-200 bg-white hover:border-gray-300`,
    
    // Success/Error styles
    success: `bg-green-50 border-green-200 text-green-800`,
    error: `bg-red-50 border-red-200 text-red-800`,
    
    // Inline styles for dynamic colors
    inlineStyles: {
      pageBg: { backgroundColor: colors.background },
      cardBg: { backgroundColor: colors.cardBg },
      textPrimary: { color: colors.text },
      textSecondary: { color: `${colors.text}CC` }, // 80% opacity
      border: { borderColor: colors.border },
      buttonPrimary: { backgroundColor: colors.buttonPrimary, color: 'white' },
      buttonPrimaryHover: { backgroundColor: colors.buttonPrimaryHover },
      buttonSecondary: { backgroundColor: colors.buttonSecondary, color: 'white' },
      buttonSecondaryHover: { backgroundColor: colors.buttonSecondaryHover },
      buttonSuccess: { backgroundColor: colors.success, color: 'white' },
      progressActive: { backgroundColor: colors.primary, color: 'white' },
      progressInactive: { backgroundColor: colors.border, color: `${colors.text}99` }, // 60% opacity
      selected: { borderColor: colors.primary, backgroundColor: `${colors.primary}1A` }, // 10% opacity
      unselected: { borderColor: colors.border, backgroundColor: colors.cardBg },
      success: { backgroundColor: colors.successBg, borderColor: colors.success, color: colors.success },
      error: { backgroundColor: colors.errorBg, borderColor: colors.error, color: colors.error },
    }
  };
}; 